import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { CalendarDays, ChevronLeft, ChevronRight, Repeat, Video } from 'lucide-react';
import { useMemo } from 'react';
import { DayView, EVERYONE } from '~/features/calendar/DayView';
import { RUN_TONE } from '~/features/calendar/runs';
import { clockMinutes } from '~/features/calendar/timeline';
import { WeekView } from '~/features/calendar/WeekView';
import { useMe } from '~/api/auth';
import { type Meeting, meetingsQueryOptions } from '~/api/meetings';
import { useServerClock } from '~/api/serverClock';
import {
  type Occurrence,
  occurrencesQueryOptions,
  type PlannedEntry,
  plannedQueryOptions,
} from '~/api/work';
import { holidaysQueryOptions } from '~/api/workspace';
import { OccurrenceRow } from '~/features/work/OccurrenceRow';
import { OccurrenceSheet } from '~/features/work/OccurrenceSheet';
import { TaskSheet } from '~/features/work/TaskSheet';
import { listsQueryOptions } from '~/api/work';
import { useWeekStart } from '~/lib/appearance';
import {
  addDays,
  calendarDate,
  describeDue,
  formatDayMonth,
  formatLongDate,
  formatMonthYear,
  formatTime,
  isoWeekdayOf,
  WEEKDAY_SHORT,
} from '~/lib/format';
import { Button } from '~/ui/Button';
import { Card } from '~/ui/Card';
import { EmptyState } from '~/ui/EmptyState';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { PageHeader } from '~/ui/PageHeader';
import { Skeleton } from '~/ui/Skeleton';

type View = 'month' | 'week' | 'day';
const VIEWS: { value: View; label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
];

interface Search {
  /** Month when left out. */
  view?: Exclude<View, 'month'>;
  /** 'YYYY-MM' */
  month?: string;
  day?: string;
  /** Whose day the day view draws: a person, or everyone. */
  who?: string;
  run?: string;
  task?: string;
}

export const Route = createFileRoute('/_app/calendar')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    view: s['view'] === 'week' || s['view'] === 'day' ? s['view'] : undefined,
    month:
      typeof s['month'] === 'string' && /^\d{4}-\d{2}$/.test(s['month']) ? s['month'] : undefined,
    day:
      typeof s['day'] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s['day']) ? s['day'] : undefined,
    who: typeof s['who'] === 'string' && s['who'] ? s['who'] : undefined,
    run: typeof s['run'] === 'string' && s['run'] ? s['run'] : undefined,
    task: typeof s['task'] === 'string' && s['task'] ? s['task'] : undefined,
  }),
  component: CalendarPage,
});

const lastDayOf = (month: string): string => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${month}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
};
const shiftMonth = (month: string, by: number): string => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

/**
 * Duties, deadlines and holidays for everyone the caller can see, three ways:
 * a month (pick a day to see it on the right), a week of tall day columns, and
 * a day laid out on the clock across the working hours.
 */
function CalendarPage() {
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/calendar' });
  const now = useServerClock(60_000);
  const tz = me.person.tz ?? me.workspace.tz;
  const today = calendarDate(now, tz);
  const [weekStart] = useWeekStart();
  const view: View = search.view ?? 'month';
  const month = search.month ?? search.day?.slice(0, 7) ?? today.slice(0, 7);
  const first = `${month}-01`;
  const last = lastDayOf(month);
  const selected = search.day ?? (today.slice(0, 7) === month ? today : first);
  const go = (patch: Partial<Search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const startOffset = weekStart === 'monday' ? 1 : 7;
  const weekOf = (d: string) => addDays(d, -((isoWeekdayOf(d) - startOffset + 7) % 7));
  const week = Array.from({ length: 7 }, (_, i) => addDays(weekOf(selected), i));
  const range =
    view === 'month'
      ? { from: addDays(first, -7), to: addDays(last, 7) }
      : view === 'week'
        ? { from: week[0]!, to: week[6]! }
        : { from: selected, to: selected };

  const occurrences = useQuery(occurrencesQueryOptions(range));
  // Repeating duties past the runs made so far (about two weeks ahead), from their rules.
  const planned = useQuery(plannedQueryOptions(range));
  const holidays = useQuery(
    holidaysQueryOptions(view === 'month' ? { from: first, to: last } : range),
  );
  // Meetings in the person's chats over the same days (instants; grouped by local day below).
  const meetings = useQuery(
    meetingsQueryOptions(
      new Date(`${range.from}T00:00:00Z`).toISOString(),
      new Date(`${addDays(range.to, 1)}T00:00:00Z`).toISOString(),
    ),
  );
  const lists = useQuery(listsQueryOptions);

  const workingDays = me.workspace.defaultSchedule.days;
  const byDate = useMemo(() => {
    const map = new Map<string, Occurrence[]>();
    const put = (date: string, o: Occurrence) => map.set(date, [...(map.get(date) ?? []), o]);
    for (const o of occurrences.data ?? []) {
      if (!o.scheduledDate) continue;
      put(o.scheduledDate, o);
      if (!o.endDate) continue;
      // Work over several days is on every working day of its span, until the day it finished.
      const finished = o.completedAt ? calendarDate(new Date(o.completedAt), tz) : null;
      const last = finished && finished < o.endDate ? finished : o.endDate;
      if (o.status === 'skipped') continue;
      for (let d = addDays(o.scheduledDate, 1); d <= last; d = addDays(d, 1)) {
        if (workingDays.includes(isoWeekdayOf(d))) put(d, o);
      }
    }
    return map;
  }, [occurrences.data, tz, workingDays]);
  // Only the days with no run yet: a day with a run shows the run.
  const plannedByDate = useMemo(() => {
    const map = new Map<string, PlannedEntry[]>();
    for (const e of planned.data ?? []) {
      if (e.runId) continue;
      map.set(e.date, [...(map.get(e.date) ?? []), e]);
    }
    return map;
  }, [planned.data]);
  const meetingsByDate = useMemo(() => {
    const map = new Map<string, Meeting[]>();
    for (const m of meetings.data ?? []) {
      if (m.status === 'cancelled') continue;
      const d = calendarDate(new Date(m.startsAt), tz);
      map.set(d, [...(map.get(d) ?? []), m]);
    }
    return map;
  }, [meetings.data, tz]);
  const holidayByDate = useMemo(
    () => new Map((holidays.data ?? []).map((h) => [h.date, h.name])),
    [holidays.data],
  );

  // The grid: whole weeks from the one containing the 1st to the one containing the last day.
  const gridStart = weekOf(first);
  const cells: string[] = [];
  for (
    let d = gridStart;
    cells.length < 42 && (d <= last || cells.length % 7 !== 0);
    d = addDays(d, 1)
  )
    cells.push(d);
  const headers = Array.from({ length: 7 }, (_, i) => WEEKDAY_SHORT[(startOffset - 1 + i) % 7]!);

  const dayRows = byDate.get(selected) ?? [];
  const dayPlans = plannedByDate.get(selected) ?? [];
  const dayMeetings = meetingsByDate.get(selected) ?? [];
  const upcoming = (occurrences.data ?? [])
    .filter(
      (o) =>
        o.scheduledDate &&
        o.scheduledDate > today &&
        o.scheduledDate <= addDays(today, 7) &&
        (o.status === 'pending' || o.status === 'paused'),
    )
    .sort((a, b) =>
      (a.scheduledDate! + (a.scheduledTime ?? '')).localeCompare(
        b.scheduledDate! + (b.scheduledTime ?? ''),
      ),
    )
    .slice(0, 8);
  const serverTime = occurrences.dataUpdatedAt
    ? new Date(occurrences.dataUpdatedAt).toISOString()
    : now.toISOString();
  const schedule = me.person.schedule ?? me.workspace.defaultSchedule;
  const sees = me.person.role !== 'member';

  // Moving back and forward a month, a week or a day; a day lands on the 1st when leaving a month.
  const step = (by: -1 | 1) =>
    view === 'month'
      ? go({ month: shiftMonth(month, by), day: undefined })
      : go({ month: undefined, day: addDays(selected, view === 'week' ? 7 * by : by) });
  const unit = view === 'month' ? 'month' : view === 'week' ? 'week' : 'day';
  const heading =
    view === 'month'
      ? formatMonthYear(first)
      : view === 'week'
        ? `${formatDayMonth(week[0]!)} – ${formatDayMonth(week[6]!)} ${week[6]!.slice(0, 4)}`
        : formatLongDate(new Date(`${selected}T12:00:00Z`), 'UTC');
  const openDay = (date: string) => go({ view: 'day', day: date, month: undefined });

  return (
    <div className="mx-auto max-w-[1400px]">
      <PageHeader
        eyebrow="Plan"
        title="Calendar"
        subtitle="Duties, deadlines and holidays, by day."
        actions={
          <div className="flex items-center gap-2">
            <div
              role="tablist"
              aria-label="Calendar view"
              data-tour="cal-views"
              className="inline-flex rounded-control border border-line bg-paper-2 p-0.5"
            >
              {VIEWS.map((v) => {
                const on = v.value === view;
                return (
                  <button
                    key={v.value}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() =>
                      go({
                        view: v.value === 'month' ? undefined : v.value,
                        day: selected,
                        month: undefined,
                      })
                    }
                    className={`h-8 rounded-[6px] px-3 text-sm transition-colors ${
                      on ? 'bg-paper font-medium text-ink shadow-card' : 'text-ink-2 hover:text-ink'
                    }`}
                  >
                    {v.label}
                  </button>
                );
              })}
            </div>
            <Button
              variant="secondary"
              data-tour="cal-today"
              onClick={() => go({ month: undefined, day: undefined })}
              icon={<CalendarDays size={16} aria-hidden />}
            >
              Today
            </Button>
          </div>
        }
      />

      <div
        className={`grid grid-cols-1 gap-4 ${view === 'month' ? 'xl:grid-cols-[minmax(0,1fr)_340px]' : ''}`}
      >
        <Card padded={false}>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <h2 className="text-base">{heading}</h2>
            <div className="flex items-center gap-1" data-tour="cal-nav">
              <button
                type="button"
                aria-label={`Previous ${unit}`}
                onClick={() => step(-1)}
                className="grid size-8 place-items-center rounded-control text-ink-2 hover:bg-paper-2"
              >
                <ChevronLeft size={16} aria-hidden />
              </button>
              <button
                type="button"
                aria-label={`Next ${unit}`}
                onClick={() => step(1)}
                className="grid size-8 place-items-center rounded-control text-ink-2 hover:bg-paper-2"
              >
                <ChevronRight size={16} aria-hidden />
              </button>
            </div>
          </div>
          {occurrences.isError ? (
            <div className="p-4">
              <ErrorNotice error={occurrences.error} onRetry={() => occurrences.refetch()} />
            </div>
          ) : view === 'week' ? (
            <WeekView
              days={week}
              byDate={byDate}
              plannedByDate={plannedByDate}
              holidayByDate={holidayByDate}
              today={today}
              workingDays={workingDays}
              showPerson={sees}
              loading={occurrences.isPending}
              onOpenRun={(id) => go({ run: id })}
              onOpenTask={(id) => go({ task: id })}
              onOpenDay={openDay}
            />
          ) : view === 'day' ? (
            <DayView
              date={selected}
              runs={byDate.get(selected) ?? []}
              planned={dayPlans}
              holiday={holidayByDate.get(selected) ?? null}
              off={!workingDays.includes(isoWeekdayOf(selected))}
              today={today}
              now={now}
              tz={tz}
              hours={{ start: clockMinutes(schedule.start), end: clockMinutes(schedule.end) }}
              me={me}
              who={search.who ?? (sees ? EVERYONE : me.person.id)}
              onWho={(id) => go({ who: id })}
              onOpenRun={(id) => go({ run: id })}
              onOpenTask={(id) => go({ task: id })}
              loading={occurrences.isPending}
            />
          ) : (
            <div className="grid grid-cols-7 border-t border-line" data-tour="cal-month">
              {headers.map((h) => (
                <div
                  key={h}
                  className="border-b border-line px-2 py-1.5 text-center text-[11px] font-medium text-ink-3 uppercase"
                >
                  {h}
                </div>
              ))}
              {cells.map((date) => {
                const inMonth = date.slice(0, 7) === month;
                const rows = byDate.get(date) ?? [];
                const plans = plannedByDate.get(date) ?? [];
                const calls = meetingsByDate.get(date) ?? [];
                const holiday = holidayByDate.get(date);
                const room = Math.max(0, (holiday ? 2 : 3) - calls.length);
                const planRoom = Math.max(0, room - rows.length);
                const count = rows.length + plans.length;
                const isToday = date === today;
                const isSelected = date === selected;
                return (
                  <button
                    key={date}
                    type="button"
                    onClick={() => go({ day: date })}
                    onDoubleClick={() => openDay(date)}
                    aria-label={`${formatLongDate(new Date(`${date}T12:00:00Z`), 'UTC')}${count ? `, ${count} duties` : ''}${holiday ? `, ${holiday}` : ''}`}
                    aria-pressed={isSelected}
                    className={`flex min-h-[88px] flex-col gap-1 border-b border-r border-line p-1.5 text-left align-top transition-colors last:border-r-0 [&:nth-child(7n+7)]:border-r-0 ${
                      inMonth ? '' : 'bg-paper-2/40 text-ink-3'
                    } ${isSelected ? 'bg-accent-soft/40' : 'hover:bg-paper-2/60'}`}
                  >
                    <span
                      className={`inline-grid size-6 place-items-center rounded-full font-mono text-xs tabular ${isToday ? 'bg-accent text-on-accent' : ''}`}
                    >
                      {Number(date.slice(8, 10))}
                    </span>
                    {holiday && (
                      <span className="truncate rounded-[4px] bg-warn-soft px-1 text-[11px] text-warn">
                        {holiday}
                      </span>
                    )}
                    {calls.slice(0, holiday ? 2 : 3).map((m) => (
                      <span
                        key={m.id}
                        className="flex items-center gap-1 truncate rounded-[4px] border border-accent/40 bg-paper px-1 text-[11px] text-accent-ink"
                      >
                        <Video size={10} aria-hidden className="shrink-0" />
                        <span className="truncate">
                          {formatTime(m.startsAt, tz)} {m.title}
                        </span>
                      </span>
                    ))}
                    {rows.slice(0, room).map((o) => (
                      <span
                        key={o.id}
                        className={`truncate rounded-[4px] px-1 text-[11px] ${RUN_TONE[o.status].chip}`}
                      >
                        {o.scheduledTime ? `${o.scheduledTime} ` : ''}
                        {o.taskTitle}
                      </span>
                    ))}
                    {plans.slice(0, planRoom).map((e) => (
                      <span
                        key={e.taskId}
                        className="flex items-center gap-1 truncate rounded-[4px] border border-dashed border-accent/45 px-1 text-[11px] text-ink-2"
                      >
                        <Repeat size={9} aria-hidden className="shrink-0" />
                        <span className="truncate">
                          {e.time ? `${e.time} ` : ''}
                          {e.taskTitle}
                        </span>
                      </span>
                    ))}
                    {count > room && (
                      <span className="text-[11px] text-ink-3">+{count - room} more</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div
            className={`flex flex-wrap gap-3 px-4 py-3 text-[11px] text-ink-3 ${view === 'day' ? 'hidden' : ''}`}
          >
            <Legend swatch="bg-accent-soft" label="Duty" />
            <Legend swatch="bg-accent" label="Running" />
            <Legend swatch="bg-done-soft" label="Done" />
            <Legend swatch="bg-late-soft" label="Missed" />
            <Legend swatch="border border-dashed border-accent/60 bg-paper" label="Planned" />
            <Legend swatch="bg-warn-soft" label="Holiday" />
            {view === 'month' && (
              <Legend swatch="border border-accent/40 bg-paper" label="Meeting" />
            )}
          </div>
        </Card>

        {view === 'month' && (
          <div className="flex flex-col gap-4">
            <Card
              title={selected === today ? 'Today' : describeDue(selected, today)}
              eyebrow={formatLongDate(new Date(`${selected}T12:00:00Z`), 'UTC')}
              dataTour="cal-detail"
              delay={60}
              actions={
                <Button variant="secondary" size="sm" onClick={() => openDay(selected)}>
                  Open day
                </Button>
              }
            >
              {dayMeetings.length > 0 && (
                <ul className="mb-3 flex flex-col gap-1.5" aria-label="Meetings">
                  {dayMeetings.map((m) => (
                    <li
                      key={m.id}
                      className="flex items-center gap-2.5 rounded-control border border-accent/30 bg-accent-soft/40 px-2.5 py-2"
                    >
                      <Video size={16} aria-hidden className="shrink-0 text-accent" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{m.title}</span>
                        <span className="block text-xs text-ink-3">
                          {formatTime(m.startsAt, tz)} – {formatTime(m.endsAt, tz)} ·{' '}
                          <Link
                            to="/chat/$id"
                            params={{ id: m.conversationId }}
                            className="text-accent hover:underline"
                          >
                            Open the chat
                          </Link>
                        </span>
                      </span>
                      {!m.endedAt && new Date(m.endsAt).getTime() > now.getTime() && (
                        <a
                          href={m.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-8 shrink-0 items-center rounded-control bg-accent px-3 text-sm font-medium text-on-accent hover:bg-accent-ink"
                        >
                          Join
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {occurrences.isPending ? (
                <Skeleton lines={4} />
              ) : dayRows.length === 0 &&
                (dayMeetings.length > 0 || dayPlans.length > 0) ? null : dayRows.length === 0 ? (
                <p className="text-sm text-ink-3">
                  {holidayByDate.get(selected)
                    ? `${holidayByDate.get(selected)}. Nothing scheduled.`
                    : 'Nothing scheduled.'}
                </p>
              ) : (
                <ul className="divide-y divide-line">
                  {dayRows.map((o) => (
                    <li key={o.id} className="-mx-1 rounded-control px-1 hover:bg-paper-2/60">
                      <OccurrenceRow
                        occurrence={o}
                        date={selected}
                        serverTime={serverTime}
                        showPerson={me.person.role !== 'member'}
                      />
                      <button
                        type="button"
                        onClick={() => go({ run: o.id })}
                        className="-mt-1 mb-2 ml-11 text-xs text-accent hover:underline"
                      >
                        Open
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {dayPlans.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1.5" aria-label="Planned">
                  {dayPlans.map((e) => (
                    <li key={e.taskId}>
                      <button
                        type="button"
                        onClick={() => go({ task: e.taskId })}
                        className="flex w-full items-center gap-2.5 rounded-control border border-dashed border-accent/45 px-2.5 py-2 text-left hover:bg-paper-2/60"
                      >
                        <Repeat size={16} aria-hidden className="shrink-0 text-accent" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{e.taskTitle}</span>
                          <span className="block text-xs text-ink-3">
                            Planned · {e.time ?? 'any time'}
                            {sees && e.personName ? ` · ${e.personName}` : ''}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Coming up" eyebrow="Next 7 days" delay={120}>
              {upcoming.length === 0 ? (
                <EmptyState title="Nothing scheduled in the coming week." />
              ) : (
                <ul className="divide-y divide-line">
                  {upcoming.map((o) => (
                    <li key={o.id}>
                      <button
                        type="button"
                        onClick={() => go({ run: o.id })}
                        className="-mx-2 flex w-full items-center gap-3 rounded-control px-2 py-2 text-left hover:bg-paper-2"
                      >
                        <span className="grid size-10 shrink-0 place-items-center rounded-panel bg-paper-2 text-center leading-none">
                          <span className="text-[10px] text-ink-3 uppercase">
                            {WEEKDAY_SHORT[isoWeekdayOf(o.scheduledDate!) - 1]}
                          </span>
                          <span className="font-display text-base font-semibold tabular">
                            {Number(o.scheduledDate!.slice(8, 10))}
                          </span>
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{o.taskTitle}</span>
                          <span className="block text-xs text-ink-3">
                            {o.scheduledTime ?? 'any time'}
                            {me.person.role !== 'member' && o.personName
                              ? ` · ${o.personName}`
                              : ''}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}
      </div>

      <OccurrenceSheet
        id={search.run ?? null}
        today={today}
        onClose={() => go({ run: undefined })}
        onOpenTask={(id) => go({ run: undefined, task: id })}
      />
      <TaskSheet
        id={search.task ?? null}
        lists={lists.data ?? []}
        today={today}
        onClose={() => go({ task: undefined })}
        onOpenRun={(id) => go({ task: undefined, run: id })}
        onOpenTask={(id) => go({ task: id })}
      />
    </div>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className={`size-2.5 rounded-[3px] ${swatch}`} />
      {label}
    </span>
  );
}
